import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, X, MapPin } from "lucide-react";
import { useLang } from "@/i18n";
import { Reveal } from "@/components/site/motion";
import { OFFICES, CONTACT } from "@/data/content";

export default function Offices() {
    const { t } = useLang();
    const o = t.offices;
    const [active, setActive] = useState(0);
    const [lightbox, setLightbox] = useState(null);

    return (
        <section data-testid="offices-section" className="py-24 md:py-32 bg-cb-light overflow-hidden">
            <div className="max-w-7xl mx-auto px-6">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
                    <div>
                        <Reveal className="text-cb-accent text-xs uppercase tracking-[0.3em] mb-4">{o.chapter}</Reveal>
                        <Reveal as="h2" delay={0.4} className="font-display text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-cb-dark">{o.title}</Reveal>
                    </div>
                    <Reveal delay={0.6} className="text-slate-600 max-w-sm">{o.sub}</Reveal>
                </div>

                {/* Interactive expanding gallery */}
                <Reveal delay={0.3}>
                    <div className="flex flex-col lg:flex-row gap-3 lg:h-[520px]">
                        {OFFICES.map((img, i) => {
                            const info = o.items[i];
                            const isActive = active === i;
                            return (
                                <motion.div
                                    key={i}
                                    data-testid={`office-panel-${i}`}
                                    onMouseEnter={() => setActive(i)}
                                    onClick={() => setLightbox(i)}
                                    animate={{ flexGrow: isActive ? 3.2 : 1 }}
                                    transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                                    className="group relative overflow-hidden cursor-pointer basis-0 min-h-[260px] lg:min-h-0"
                                    style={{ flexGrow: isActive ? 3.2 : 1 }}
                                >
                                    <img
                                        src={img}
                                        alt={info.title}
                                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                    />
                                    <div className={`absolute inset-0 transition-colors duration-500 ${isActive ? "bg-gradient-to-t from-cb-darker/90 via-cb-darker/20 to-transparent" : "bg-cb-darker/55"}`} />

                                    {/* Number */}
                                    <span className="absolute top-5 left-5 font-display text-sm font-semibold text-white/70">
                                        0{i + 1}
                                    </span>

                                    {/* Expand icon */}
                                    <span className="absolute top-4 right-4 bg-white/15 backdrop-blur-sm text-white p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                        <Plus size={16} />
                                    </span>

                                    {/* Caption */}
                                    <div className="absolute inset-x-0 bottom-0 p-6">
                                        <span className="inline-block text-cb-accent text-xs uppercase tracking-[0.25em] mb-2">{info.tag}</span>
                                        <h3 className="font-display text-xl md:text-2xl font-semibold text-white">{info.title}</h3>
                                        <AnimatePresence>
                                            {isActive && (
                                                <motion.p
                                                    initial={{ opacity: 0, height: 0 }}
                                                    animate={{ opacity: 1, height: "auto" }}
                                                    exit={{ opacity: 0, height: 0 }}
                                                    transition={{ duration: 0.4 }}
                                                    className="mt-3 max-w-md text-sm text-slate-200 leading-relaxed overflow-hidden"
                                                >
                                                    {info.desc}
                                                </motion.p>
                                            )}
                                        </AnimatePresence>
                                    </div>

                                    <span className={`absolute bottom-0 left-0 h-1 bg-cb-accent transition-all duration-500 ${isActive ? "w-full" : "w-0"}`} />
                                </motion.div>
                            );
                        })}
                    </div>
                </Reveal>

                <Reveal delay={0.5} className="mt-8 flex items-center gap-2 text-sm text-slate-500">
                    <MapPin size={16} className="text-cb-accent" /> {CONTACT.address}
                </Reveal>
            </div>

            {/* Lightbox */}
            <AnimatePresence>
                {lightbox !== null && (
                    <motion.div
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        onClick={() => setLightbox(null)}
                        data-testid="office-lightbox" role="dialog" aria-modal="true"
                        className="fixed inset-0 z-[80] bg-cb-darker/92 backdrop-blur-sm flex items-center justify-center p-4 md:p-10"
                    >
                        <motion.div
                            initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0 }}
                            onClick={(e) => e.stopPropagation()}
                            className="relative bg-white max-w-5xl w-full max-h-[90vh] overflow-hidden"
                        >
                            <button data-testid="office-lightbox-close" onClick={() => setLightbox(null)}
                                className="absolute top-4 right-4 z-10 bg-white/90 p-2 text-cb-dark hover:bg-cb-accent hover:text-white transition-colors">
                                <X size={20} />
                            </button>
                            <img src={OFFICES[lightbox]} alt={o.items[lightbox].title} className="w-full max-h-[70vh] object-contain bg-cb-dark" />
                            <div className="p-6 md:p-8">
                                <span className="text-cb-accent text-xs uppercase tracking-[0.25em]">{o.items[lightbox].tag}</span>
                                <h3 className="font-display text-2xl font-bold text-cb-dark mt-1">{o.items[lightbox].title}</h3>
                                <p className="mt-2 text-slate-600 leading-relaxed">{o.items[lightbox].desc}</p>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
}
